import { Box, Card, Divider, Skeleton, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

const ROLE_TILES = 2;
const PERKS = 3;
const CONTACT_WIDTHS = ['55%', '35%', '45%', '30%'];
const CHIP_WIDTHS = [72, 96, 150, 80, 36];

/** Loading state shaped like InvitationClinicCard with one invitation and two roles. */
export default function InvitationClinicCardSkeleton(): JSX.Element {
  const { t } = useTranslation();
  return (
    <Card
      aria-busy
      aria-label={String(t('invitationAcceptance.loading'))}
      sx={{ overflow: 'hidden' }}
    >
      <Box sx={{ position: 'relative', mb: { xs: 5, md: 6 } }}>
        <Skeleton
          variant="rectangular"
          sx={{ height: { xs: 176, md: 280 }, borderRadius: 0 }}
        />
        <Skeleton
          variant="rounded"
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            width: 176,
            height: 24,
            bgcolor: 'background.default',
          }}
        />
        <Skeleton
          variant="rounded"
          sx={(theme) => ({
            position: 'absolute',
            left: { xs: 16, md: 24 },
            bottom: { xs: -36, md: -48 },
            width: { xs: 72, md: 96 },
            height: { xs: 72, md: 96 },
            borderRadius: 3,
            border: `4px solid ${theme.palette.background.paper}`,
            bgcolor: 'background.neutral',
          })}
        />
      </Box>
      <Stack gap={3} sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 } }}>
        <Box>
          <Skeleton variant="text" sx={{ typography: 'h4', width: '60%' }} />
          {/* Clinic names usually wrap on phones. */}
          <Skeleton
            variant="text"
            sx={{ typography: 'h4', width: '45%', display: { md: 'none' } }}
          />
          <Skeleton variant="text" sx={{ width: '40%', typography: 'body2' }} />
        </Box>
        <Stack gap={1.5}>
          <Skeleton variant="text" sx={{ width: 100 }} />
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                md: 'repeat(2, minmax(0, 1fr))',
              },
            }}
          >
            {Array.from({ length: ROLE_TILES }, (_, tile) => (
              <Stack
                key={tile}
                gap={2}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Stack direction="row" alignItems="center" gap={1.5}>
                  <Skeleton
                    variant="rounded"
                    sx={{
                      width: 44,
                      height: 44,
                      flexShrink: 0,
                      borderRadius: 1.5,
                    }}
                  />
                  <Skeleton variant="text" sx={{ width: 120 }} />
                </Stack>
                <Skeleton
                  variant="text"
                  sx={{ width: '70%', typography: 'body2' }}
                />
                <Stack gap={1}>
                  {Array.from({ length: PERKS }, (__, perk) => (
                    <Stack
                      key={perk}
                      direction="row"
                      alignItems="center"
                      gap={1}
                    >
                      <Skeleton
                        variant="circular"
                        sx={{ width: 18, height: 18, flexShrink: 0 }}
                      />
                      <Skeleton
                        variant="text"
                        sx={{
                          width: `${70 - perk * 10}%`,
                          typography: 'body2',
                        }}
                      />
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            ))}
          </Box>
        </Stack>
        <Stack gap={1}>
          <Skeleton
            variant="text"
            sx={{ width: '40%', typography: 'body2', display: { md: 'none' } }}
          />
          {CONTACT_WIDTHS.map((width) => (
            <Skeleton
              key={width}
              variant="text"
              sx={{ width, typography: 'body2' }}
            />
          ))}
        </Stack>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {CHIP_WIDTHS.map((width) => (
            <Skeleton
              key={width}
              variant="rounded"
              sx={{ width, height: 24, borderRadius: 2 }}
            />
          ))}
        </Stack>
        <Divider />
        <Skeleton variant="rounded" sx={{ height: 56, borderRadius: 1 }} />
        <Stack direction={{ xs: 'column', md: 'row-reverse' }} gap={1}>
          <Skeleton
            variant="rounded"
            sx={{ height: 48, flex: { md: 1 }, borderRadius: 1 }}
          />
          <Skeleton
            variant="rounded"
            sx={{ height: 48, width: { md: 190 }, borderRadius: 1 }}
          />
        </Stack>
      </Stack>
    </Card>
  );
}
